import { useEffect, useState } from "react";
import { CustomModal, Button, showToast } from "../../shared";
import { HiOutlineClipboard, HiOutlineClipboardDocumentCheck } from "react-icons/hi2";

const EXPIRY_OPTIONS = [
  { value: 1, label: "1 hour" },
  { value: 24, label: "24 hours" },
  { value: 168, label: "7 days" },
  { value: 720, label: "30 days" },
  { value: -1, label: "Custom" },
];

const MAX_EXPIRY_HOURS = 720;
const CUSTOM_MIN_OFFSET_MINUTES = 5;

const pad = (value) => String(value).padStart(2, "0");

const toLocalDateTimeInput = (date) => {
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const shiftDate = (date, minutes) => new Date(date.getTime() + minutes * 60 * 1000);

export const FileShareModal = ({
  isOpen,
  onClose,
  file,
  onGenerate,
  isLoading,
  shareResult,
}) => {
  const [expiryHours, setExpiryHours] = useState(168);
  const [customExpiresAt, setCustomExpiresAt] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [copied, setCopied] = useState(false);

  const customBounds = (() => {
    const now = new Date();
    return {
      min: toLocalDateTimeInput(shiftDate(now, CUSTOM_MIN_OFFSET_MINUTES)),
      max: toLocalDateTimeInput(shiftDate(now, MAX_EXPIRY_HOURS * 60)),
    };
  })();

  useEffect(() => {
    if (!isOpen) return;
    setExpiryHours(168);
    setCustomExpiresAt(toLocalDateTimeInput(shiftDate(new Date(), 7 * 24 * 60)));
    setShowCustom(false);
    setCopied(false);
  }, [isOpen, file?.id]);

  const handleCopy = async () => {
    if (!shareResult?.share_url) return;
    try {
      await navigator.clipboard.writeText(shareResult.share_url);
      setCopied(true);
      showToast("Link copied to clipboard", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast("Failed to copy link", "error");
    }
  };

  const handleGenerate = () => {
    if (showCustom) {
      const selectedDate = customExpiresAt ? new Date(customExpiresAt) : null;
      const now = Date.now();
      if (!selectedDate || Number.isNaN(selectedDate.getTime())) {
        showToast("Choose a valid expiry date and time", "error");
        return;
      }
      if (selectedDate.getTime() <= now + CUSTOM_MIN_OFFSET_MINUTES * 60 * 1000) {
        showToast("Expiry must be in the future", "error");
        return;
      }
      if (selectedDate.getTime() > now + MAX_EXPIRY_HOURS * 60 * 60 * 1000) {
        showToast("Expiry must be within 30 days", "error");
        return;
      }
      onGenerate({ fileId: file?.id, expiresAt: selectedDate.toISOString() });
      return;
    }

    onGenerate({ fileId: file?.id, expiryHours });
  };

  const handleClose = () => {
    setCopied(false);
    onClose();
  };

  return (
    <CustomModal
      title={`Share "${file?.original_name || "file"}"`}
      open={isOpen}
      close={handleClose}
      showPrimaryButton={!shareResult}
      primaryButtonText="Generate Link"
      primaryButtonClick={handleGenerate}
      primaryButtonLoading={isLoading}
      primaryButtonDisabled={isLoading}
      showSecondaryButton={!shareResult}
      secondaryButtonText="Cancel"
      secondaryButtonClick={handleClose}
      isDismissable={!isLoading}
    >
      <div className="flex flex-col gap-4">
        {!shareResult ? (
          <>
            <div>
              <label htmlFor="shared-file-expiry" className="block text-sm font-medium text-darkgrey mb-1.5">
                Expires in
              </label>
              <select
                id="shared-file-expiry"
                value={showCustom ? -1 : expiryHours}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (val === -1) {
                    setShowCustom(true);
                  } else {
                    setShowCustom(false);
                    setExpiryHours(val);
                  }
                }}
                className="w-full px-3 py-2 text-sm border border-surface-border rounded-lg bg-white focus:border-primary/50 focus:ring-2 focus:ring-primary/10 outline-none transition-all"
              >
                {EXPIRY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {showCustom && (
                <div className="mt-3 space-y-2">
                  <input
                    type="datetime-local"
                    min={customBounds.min}
                    max={customBounds.max}
                    value={customExpiresAt}
                    onChange={(e) => setCustomExpiresAt(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-surface-border rounded-lg bg-white focus:border-primary/50 focus:ring-2 focus:ring-primary/10 outline-none"
                    aria-label="Custom expiry date"
                  />
                  <div className="flex items-center justify-between gap-3 text-xs text-grey">
                    <span>Pick a date and time within 30 days.</span>
                    <span className="whitespace-nowrap">Local time</span>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-darkgrey">
              Share this link with anyone. It expires{" "}
              {new Date(shareResult.expires_at).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
              .
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={shareResult.share_url}
                className="flex-1 px-3 py-2 text-sm border border-surface-border rounded-lg bg-surface-hover text-darkgrey outline-none"
                aria-label="Share link"
                onFocus={(e) => e.target.select()}
              />
              <Button
                variant="filled"
                color="secondary"
                size="md"
                onClick={handleCopy}
                icon={copied ? HiOutlineClipboardDocumentCheck : HiOutlineClipboard}
                iconSize={16}
              >
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  );
};
